import {Fragment, useCallback, useState} from "react";
import PlayerNameWithFlag from "../../players/PlayerNameWithFlag";
import MatchBox from "../../matches/MatchBox";
import {ROUND_STATES} from "../../../util/Constants";
import {isMatchFinished, sortMatches} from "../../../util/HelperUtils";
import {useIsMobile} from "../../../hooks/useIsMobile";
import {InputGroup, Tab, Tabs, Table, OverlayTrigger, Popover} from "react-bootstrap";
import {useTranslation} from "react-i18next";
import Form from "react-bootstrap/Form";
import {getLoggedUser} from "../../../util/SecurityUtils";
import {useNavigate} from "react-router-dom";
import {useBlastQueue} from "../../../hooks/queries/useTournament";
import {useBlastBenchPlayer, useBlastReturnPlayer} from "../../../hooks/mutations/useTournamentMutations";
import type {Match, Player, PlayerScore, PlayerWithSeeding, TournamentRound} from "../../../types/domain";

interface BlastRoundTabProps {
  round: TournamentRound;
  onMatchClick: (match: Match) => void;
  players: PlayerWithSeeding[];
  tournamentId: number;
  canManage: boolean;
}

function BlastRoundTab(props: BlastRoundTabProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [ matchFilter, setMatchFilter ] = useState('');

  const { data: queue = [] } = useBlastQueue(props.tournamentId);
  const benchPlayerMutation = useBlastBenchPlayer();
  const returnPlayerMutation = useBlastReturnPlayer();

  const isInProgress = (round: TournamentRound) => {
    return round.state === ROUND_STATES.IN_PROGRESS;
  };

  const handleOnClick = useCallback((id: number) => navigate('/players/' + id), [navigate]);

  const handleToggleBench = useCallback(async (playerId: number, isOnBench: boolean) => {
    try {
      const tournamentId = props.tournamentId;
      if (isOnBench) {
        await returnPlayerMutation.mutateAsync({ tournamentId, playerId });
      } else {
        await benchPlayerMutation.mutateAsync({ tournamentId, playerId });
      }
    } catch (error) {
      console.error("Error toggling bench:", error);
    }
  }, [props.tournamentId, benchPlayerMutation, returnPlayerMutation]);

  const round = props.round;
  const sortedMatches = sortMatches(round.matches, matchFilter);

  const playedMatches = sortedMatches.filter(match => isMatchFinished(match.state));
  const unfinishedMatches = sortedMatches.filter(match => !isMatchFinished(match.state));

  const getBuchholzPopover = () => {
    return (
      <Popover style={{maxHeight: "9rem", backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)'}}>
        <Popover.Header as="h3" style={{ backgroundColor: 'var(--bg-tertiary)', color: 'var(--text-primary)' }}>{t("buchholzScore")}</Popover.Header>
        <Popover.Body style={{ color: 'var(--text-secondary)' }}>
          {t("buchholzScoreDescription")}
        </Popover.Body>
      </Popover>
    );
  };

  const getPointsClassName = (playerScore: PlayerScore) => {
    if (playerScore.pointsWon - playerScore.pointsLost > 0) {
      return "win";
    } else if (playerScore.pointsWon - playerScore.pointsLost < 0) {
      return "loss";
    } else {
      return "tie";
    }
  };

  const isPlayerOnBench = (playerId: number): boolean => {
    const tournamentPlayer = props.players?.find(p => p.player.id === playerId);
    return tournamentPlayer?.onBench || false;
  };

  const renderBenchIcon = (player: Player) => {
    if (!props.canManage) return null;

    const onBench = isPlayerOnBench(player.id);

    return (
      <span
        onClick={(e) => { e.stopPropagation(); handleToggleBench(player.id, onBench); }}
        style={{
          cursor: 'pointer',
          marginLeft: '8px',
          opacity: onBench ? 1 : 0.4
        }}
        title={onBench ? t("returnFromBench") : t("moveToBench")}
      >
        {onBench ? '🪑' : '🪑'}
      </span>
    );
  };

  const getRowsFromScores = (scores: PlayerScore[]) => {
    return scores.map((playerScore, index) => {
      const player = playerScore.player;
      const onBench = isPlayerOnBench(player.id);
      const isLoggedUser = getLoggedUser()?.playerId === player.id;
      const highlightedTop = isLoggedUser ? " highlighted-score-top" : "";
      const highlightedBottom = isLoggedUser ? " highlighted-score-bottom" : "";
      const highlighted = highlightedTop + highlightedBottom;
      const benchStyle = onBench ? { opacity: 0.4, color: 'var(--text-tertiary)' } : {};

      return (
        <Fragment key={"blast-score-" + player.id + "-" + index}>
          <tr className={"hide-on-mobile clickable" + highlighted}
              style={benchStyle}
              onClick={() => handleOnClick(player.id)}>
            <th>{index + 1}.</th>
            <th style={{"textAlign": "left"}}>
              <PlayerNameWithFlag player={player} includeElo={true} aliasOnly={true} />
            </th>
            {props.canManage && (
              <th style={{width: "40px"}} onClick={(e) => e.stopPropagation()}>
                {renderBenchIcon(player)}
              </th>
            )}
            <th style={{width: "100px"}}>
              <span style={{fontWeight: "bold"}}>{playerScore.score}{t("pointAbbreviation")} </span>
              <span style={{color: "var(--text-muted)", fontSize: "small"}}> ({playerScore.buchholz})</span>
            </th>
            <th style={{width: "80px"}}>
              <span className="win">{playerScore.matchesWon}</span>
              <span className="tie"> - </span>
              <span className="loss">{playerScore.matchesLost}</span>
            </th>
            <th style={{width: "80px"}}>
              <span className="win">{playerScore.setsWon}</span>
              <span className="tie"> - </span>
              <span className="loss">{playerScore.setsLost}</span>
            </th>
            <th style={{width: "110px"}}>
              <span className={getPointsClassName(playerScore)}>{playerScore.pointsWon > playerScore.pointsLost ? "+" : ""}{playerScore.pointsWon - playerScore.pointsLost}</span>
              <span style={{fontSize: "small"}}> (</span>
              <span className="win" style={{fontSize: "small"}}>{playerScore.pointsWon}</span>
              <span className="tie" style={{fontSize: "small"}}> - </span>
              <span className="loss" style={{fontSize: "small"}}>{playerScore.pointsLost}</span>
              <span style={{fontSize: "small"}}>)</span>
            </th>
          </tr>
          <tr className={"hide-on-desktop clickable" + highlightedTop}
              style={benchStyle}
              onClick={() => handleOnClick(player.id)}>
            <th>{index + 1}.</th>
            <th style={{"textAlign": "left"}} colSpan={props.canManage ? 8 : 9}>
              <PlayerNameWithFlag player={player} includeElo={true} aliasOnly={true} />
            </th>
            {props.canManage && (
              <th style={{width: "40px"}} onClick={(e) => e.stopPropagation()}>
                {renderBenchIcon(player)}
              </th>
            )}
          </tr>
          <tr className="hide-on-mobile hide-on-desktop" />
          <tr className={"hide-on-desktop clickable" + highlightedBottom}
              style={benchStyle}
              onClick={() => handleOnClick(player.id)}>
            <th style={{width: "100px"}}>
              <span style={{fontWeight: "bold"}}>{playerScore.score}{t("pointAbbreviation")} </span>
              <span style={{color: "var(--text-muted)", fontSize: "small"}}> ({playerScore.buchholz})</span>
            </th>
            <th style={{width: "80px"}}>
              <span className="win">{playerScore.matchesWon}</span>
              <span className="tie"> - </span>
              <span className="loss">{playerScore.matchesLost}</span>
            </th>
            <th style={{width: "80px"}}>
              <span className="win">{playerScore.setsWon}</span>
              <span className="tie"> - </span>
              <span className="loss">{playerScore.setsLost}</span>
            </th>
            <th style={{width: "110px"}}>
              <span className={getPointsClassName(playerScore)}>{playerScore.pointsWon > playerScore.pointsLost ? "+" : ""}{playerScore.pointsWon - playerScore.pointsLost}&nbsp;</span>
              <span style={{fontSize: "x-small"}}>(</span>
              <span className="win" style={{fontSize: "x-small"}}>{playerScore.pointsWon}</span>
              <span className="tie" style={{fontSize: "x-small"}}> - </span>
              <span className="loss" style={{fontSize: "x-small"}}>{playerScore.pointsLost}</span>
              <span style={{fontSize: "x-small"}}>)</span>
            </th>
          </tr>
          <tr className="hide-on-mobile hide-on-desktop" />
        </Fragment>
      );
    });
  };

  const renderScoreTable = () => {
    return (
      <Table striped bordered hover size="sm" className="text-center theme-table" style={{ backgroundColor: 'var(--table-row-bg)', color: 'var(--text-primary)', borderColor: 'var(--border-color)' }}>
        <thead style={{"textTransform": "none"}}>
          <tr>
            <th className="hide-on-mobile">#</th>
            <th className="hide-on-mobile">{t("player")}</th>
            {props.canManage && <th style={{width: "40px"}}></th>}
            <th style={{width: "100px"}}><OverlayTrigger placement="bottom" overlay={getBuchholzPopover()}>
              <span>{t("score")} <span style={{textDecoration: "underline"}}>(Bch)</span></span>
            </OverlayTrigger></th>
            <th style={{width: "80px"}}>{t("matches")}</th>
            <th style={{width: "80px"}}>{t("sets")}</th>
            <th style={{width: "110px"}}>{t("points")}</th>
          </tr>
        </thead>
        <tbody>
          {getRowsFromScores(round.scores)}
        </tbody>
      </Table>
    );
  };

  const renderQueue = () => {
    if (queue.length === 0) return null;

    return (
      <div className="blast-queue mt-3" style={{
        padding: '12px',
        backgroundColor: 'var(--bg-tertiary)',
        borderRadius: '8px',
        border: '1px solid var(--border-color)'
      }}>
        <h6 style={{ color: 'var(--text-primary)', marginBottom: '12px' }}>{t("queue")}</h6>
        <div className="blast-queue-items">
          {queue.map((player, index) => (
            <div
              key={player.id}
              onClick={() => handleOnClick(player.id)}
              style={{
                padding: '8px 12px',
                backgroundColor: 'var(--bg-secondary)',
                borderRadius: '4px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                border: '1px solid var(--border-color)'
              }}
            >
              <span style={{
                fontWeight: 'bold',
                color: 'var(--text-secondary)',
                minWidth: '20px'
              }}>
                {index + 1}.
              </span>
              <PlayerNameWithFlag player={player} aliasOnly={true} />
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderMatches = () => {
    return (
      <>
        <InputGroup className="mt-3 me-5 mb-1">
          <InputGroup.Text id="searchbar" style={{ backgroundColor: 'var(--bg-tertiary)', borderColor: 'var(--border-color)', color: 'var(--text-secondary)' }}>{t("search")}</InputGroup.Text>
          <Form.Control onChange={e => setMatchFilter(e.target.value)} style={{ backgroundColor: 'var(--input-bg)', borderColor: 'var(--input-border)', color: 'var(--text-primary)' }} />
        </InputGroup>
        <div className="unplayed-match-in-row">
          {unfinishedMatches.map(match => {
            return <MatchBox variant="unfinished" key={"match-box-" + match.id} match={match} onClick={() => props.onMatchClick(match)} canManage={props.canManage} />
          })}
        </div>
        {playedMatches.map(match => {
          return <MatchBox key={"match-box-" + match.id} match={match} onClick={() => props.onMatchClick(match)} indicateFinished={isInProgress(round)} canManage={props.canManage} />
        })}
      </>
    );
  };

  const mobileMatchesList = (
    <>
      <InputGroup className="mt-3 me-5 mb-1">
        <InputGroup.Text id="searchbar" style={{ backgroundColor: 'var(--bg-tertiary)', borderColor: 'var(--border-color)', color: 'var(--text-secondary)' }}>{t("search")}</InputGroup.Text>
        <Form.Control onChange={e => setMatchFilter(e.target.value)} style={{ backgroundColor: 'var(--input-bg)', borderColor: 'var(--input-border)', color: 'var(--text-primary)' }} />
      </InputGroup>
      {sortedMatches.map(match => (
        <MatchBox key={"match-box-" + match.id} match={match} onClick={() => props.onMatchClick(match)} indicateFinished={isInProgress(round)} canManage={props.canManage} />
      ))}
    </>
  );

  return (
    <div>
      {isMobile ? (
        <Tabs
          defaultActiveKey={isInProgress(round) ? "matches-tab" : "score-tab"}
          id="mobile-blast-tabs"
          variant="tabs"
          fill
          className="theme-tabs"
          style={{ borderColor: 'var(--border-color)' }}
        >
          <Tab eventKey="score-tab" title={t("scoretable")}>{renderScoreTable()}</Tab>
          <Tab eventKey="matches-tab" title={t("matches")}>{mobileMatchesList}</Tab>
          <Tab eventKey="queue-tab" title={t("queue")}>{renderQueue()}</Tab>
        </Tabs>
      ) : (
        <>
          <div className="left-half-responsive">
            {renderScoreTable()}
            {renderQueue()}
          </div>
          <div className="right-half-responsive">
            {renderMatches()}
          </div>
        </>
      )}
    </div>
  );
}

export default BlastRoundTab;
